import { ExperienceTracker } from '@gonfalon/analytics/ExperienceTracker';

const CHANGE_HISTORY_EXPERIENCE_NAME = 'Change History';

export type ChangeHistoryTrackEvent =
  | 'Copy policy statements'
  | 'Copy activity link'
  | 'Copy entry details link'
  | 'Delete resource filter'
  | 'Add resource'
  | 'Toggle expand all'
  | 'Toggle entry details inline'
  | 'Remove invalid filter'
  | 'Update search filter'
  | 'Update date filter'
  | 'Open change history entry detail'
  | 'Close change history entry detail'
  | 'Switch compare view'
  | 'Load more change history';

export const changeHistoryTracker = ExperienceTracker.create<ChangeHistoryTrackEvent>(CHANGE_HISTORY_EXPERIENCE_NAME, {
  category: 'ChangeHistory',
  owners: ['team-foundation'],
  emitLegacyMetrics: true,
  legacyMetricNames: {
    start: 'Show change history clicked',
  },
});

/** Rollback domain (flag, future entity types). Use {@link ROLLBACK_TYPE} on start/track/end attributes. */
export const ROLLBACK_TYPE = {
  flag: 'flag',
} as const;

export type RollbackType = (typeof ROLLBACK_TYPE)[keyof typeof ROLLBACK_TYPE];

const ROLLBACK_EXPERIENCE_NAME = 'Rollback';

/** What the user changed on the flag targeting form while a rollback is in progress. */
export type RollbackFlagTargetingChangeKind =
  | 'add_custom_rule'
  | 'add_generated_rule'
  | 'add_mobile_rule'
  | 'add_prerequisite'
  | 'add_rule'
  | 'add_rule_at_index'
  | 'add_rule_clause'
  | 'add_segment_rule'
  | 'bulk_clear_all_targets'
  | 'change_fallthrough_bucket'
  | 'change_fallthrough_experiment_rollout'
  | 'change_fallthrough_rollout'
  | 'change_rule_bucket'
  | 'change_rule_experiment_rollout'
  | 'change_rule_rollout'
  | 'clear_all_targets'
  | 'convert_targets_to_segment'
  | 'delete_context_targeting_expiration'
  | 'delete_prerequisite'
  | 'delete_rule'
  | 'delete_rule_clause'
  | 'disable_rule'
  | 'discard_custom_rule_changes'
  | 'discard_fallthrough_variation_changes'
  | 'discard_guarded_rollout'
  | 'discard_prerequisites_changes'
  | 'discard_targeting_changes'
  | 'duplicate_rule'
  | 'edit_prerequisite'
  | 'edit_rule_clause'
  | 'edit_rule_description'
  | 'edit_rule_track_events'
  | 'enable_rule'
  | 'initialize_guarded_rollout'
  | 'patch_context_targeting_expiration'
  | 'post_context_targeting_expiration'
  | 'reorder_rule'
  | 'select_experiment_optimization_mode'
  | 'select_fallthrough_variation'
  | 'select_off_variation'
  | 'select_percentage_variation_split'
  | 'select_progressive_release'
  | 'select_rule_variation'
  | 'select_single_variation'
  | 'set_progressive_rollout_variations'
  | 'set_target'
  | 'toggle_flag'
  | 'undo_context_targeting_expiration'
  | 'update_client_side_availability'
  | 'update_configuration_from_json'
  | 'update_expiring_values'
  | 'update_guarded_rollout';

export type RollbackTrackEvent =
  | 'Initiate rollback from compare'
  | 'Preview rollback from change history'
  | 'Error'
  | {
      eventName: 'Flag targeting change during rollback';
      attributes: {
        change_kind: RollbackFlagTargetingChangeKind;
        rollbackType: RollbackType;
        resource_key: string;
        environment_key: string;
        project_key: string;
        rollback_id: string;
      };
    };

export const rollbackTracker = ExperienceTracker.create<RollbackTrackEvent>(ROLLBACK_EXPERIENCE_NAME, {
  owners: ['team-foundation'],
  category: 'Rollback',
});

/** Ends the in-flight experience when the modal is dismissed (no-op if it was never started, e.g. deep link). */
export function endChangeHistoryExperienceIfActive(outcome: 'success' | 'error' | 'cancel' = 'cancel') {
  const hasActive = ExperienceTracker.getState().some((e) => e.experienceName === CHANGE_HISTORY_EXPERIENCE_NAME);
  if (hasActive) {
    changeHistoryTracker.end({ outcome });
  }
}
